import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Box, Tab, Tabs, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDocumentationActiveObjectData,
  selectSingleObject,
  setObjectName,
  setTab,
} from '../../../reducers/documentation/documentationReducer';
import { selectParsedRules, selectParserInfo } from '../../../reducers/global/parserReducer';
import { selectTopBarSearchText } from '../../../reducers/documentation/documentationTopBarReducer';
import { useSelectableColumns } from '../../common/table/useSelectableColumns';
import { getColumns } from '../../documentation/selected-object/expanded-mode/tableColumns';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { VirtualScrollCustomColumnsTable } from '../../common/table/custom-columns-table/VirtualScrollCustomColumnsTable';
import { CustomColumnsTableContextProvider } from '../../common/table/custom-columns-table/CustomColumnsTableContext';
import { useDocumentation } from '../../documentation/useDocumentation';
import { usePrepareActiveObjectStructure } from '../../documentation/selected-object/usePrepareActiveObjectStructure';
import useParser from '../../parser/useParser';
import { DocumentationFilters } from '../../documentation/selected-object/filters/DocumentationFilters';
import { TopBarRenderer } from '../../documentation/shared/layouts/TopBarRenderer';
import { filterItemsByQuerySearch } from '../../documentation/selected-object/filters/filtersOptions';
import {
  OBJECT_TABS,
  TAB_IDS,
  filterItemsByTab,
  createObjectSelectionItem,
  TableEmptyState,
} from './utils';
import { ParsedRule, ParserBaseRuleType } from '../../parser/ParserTypes';
import { SelectionItem } from './AgentContextSelectionModal';
import { DataTableId } from '../../common/table/utils';
import { ConfigurationItem } from '../../documentation/dependencies/DependenciesTypes';
import { DataTableBaseColumn, DataTableRow } from '../../common/table/TableTypes';

interface ObjectTabsForAgentProps {
  objectType: string;
  crmOrgId: string;
  onSelectionChange: (selections: SelectionItem[]) => void;
  isSubmitting?: boolean;
  initialSelections?: SelectionItem[];
}

interface TabSelections {
  [tabId: string]: string[];
}

export const ObjectTabsForAgent: React.FC<ObjectTabsForAgentProps> = ({
  objectType,
  crmOrgId,
  onSelectionChange,
  isSubmitting = false,
  initialSelections = [],
}) => {
  const dispatch = useDispatch();

  // Convert initial selections to tab selections format
  // Create a stable empty object for creation mode
  const EMPTY_TAB_SELECTIONS = useMemo(() => ({}), []);

  const initialTabSelections = useMemo(() => {
    // In creation mode, use a stable empty object
    if (initialSelections.length === 0) {
      return EMPTY_TAB_SELECTIONS;
    }

    // Configuration update mode - compute from initial selections
    const _tabSelections: TabSelections = {};
    OBJECT_TABS.forEach((tab) => {
      const tabItems = initialSelections.filter((selection) =>
        tab.types.includes(selection.type as any),
      );
      if (tabItems.length > 0) {
        _tabSelections[tab.id] = tabItems.map((item) => item.name);
      }
    });
    return _tabSelections;
  }, [EMPTY_TAB_SELECTIONS, initialSelections]);

  const [tabSelections, setTabSelections] = useState<TabSelections>(initialTabSelections);
  const [manualActiveTab, setManualActiveTab] = useState<number | null>(null);

  // Compute the active tab: use manual selection if set, otherwise auto-select based on selections
  const activeTab = useMemo(() => {
    // If user has manually selected a tab, use that
    if (manualActiveTab !== null) {
      return manualActiveTab;
    }

    // Otherwise, auto-select the first tab with selections
    if (Object.keys(tabSelections).length === 0) {
      return 0; // No selections, start with first tab
    }

    // Find the index of the first tab that has selections
    for (let i = 0; i < OBJECT_TABS.length; i++) {
      const tabId = OBJECT_TABS[i].id;
      if (tabSelections[tabId] && tabSelections[tabId].length > 0) {
        return i;
      }
    }
    return 0; // Fallback to first tab
  }, [tabSelections, manualActiveTab]);

  // Update tab selections when initialTabSelections change
  useEffect(() => {
    setTabSelections(initialTabSelections);

    // Only reset manual selection if we actually have initial data
    // In creation mode (no initial selections), preserve user's tab choice
    if (initialSelections.length > 0) {
      setManualActiveTab(null);
    }
  }, [initialSelections.length, initialTabSelections]);
  const [isParsingObject, setIsParsingObject] = useState(false);

  const activeObjectData = useSelector(selectDocumentationActiveObjectData);
  const parsedRules = useSelector(selectParsedRules(crmOrgId));
  const searchText = useSelector(selectTopBarSearchText);
  const currentObjectFromRedux = useSelector(selectSingleObject);
  const parsedInfo = useSelector(selectParserInfo(crmOrgId));
  const parsedObjectNames = useMemo(
    () => parsedInfo?.parsedObjectNames ?? [],
    [parsedInfo?.parsedObjectNames],
  );

  const { parseObjectOnDemand } = useParser({ crmOrgId });

  const { setActiveObjectElements } = useDocumentation();

  useEffect(() => {
    dispatch(setObjectName({ objectTypeName: { objectType, label: objectType } }));
  }, [dispatch, objectType]);

  useEffect(() => {
    const isObjectParsed = parsedObjectNames.includes(objectType);

    if (!isObjectParsed && objectType && !isParsingObject) {
      setIsParsingObject(true);

      try {
        parseObjectOnDemand(objectType);
      } catch (error: unknown) {
        console.error(`Failed to parse object ${objectType}:`, error);
        setIsParsingObject(false);
      }
    } else if (isObjectParsed && isParsingObject) {
      setIsParsingObject(false);
    }
  }, [objectType, parsedObjectNames, parseObjectOnDemand, isParsingObject]);

  // Clear selections when object type changes to prevent stale data
  // BUT don't clear if we have initial selections that should be preserved
  useEffect(() => {
    if (initialSelections.length > 0) {
      return; // Don't clear selections when we have initial data
    }

    setTabSelections({});
    setManualActiveTab(null);
    onSelectionChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, initialSelections.length]);

  const isReduxStateSynced = currentObjectFromRedux?.objectType === objectType;

  const getSafeActiveObjectData = useCallback(
    (dataType: DocumentationTabTypes) => {
      return isReduxStateSynced ? activeObjectData?.[dataType] || [] : [];
    },
    [isReduxStateSynced, activeObjectData],
  );

  const objectStructure = usePrepareActiveObjectStructure(crmOrgId);

  useEffect(() => {
    if (objectStructure && objectType && isReduxStateSynced) {
      const hasData = Object.values(objectStructure).some(
        (data: ConfigurationItem[]) => Array.isArray(data) && data.length > 0,
      );

      if (hasData || !activeObjectData) {
        setActiveObjectElements(objectStructure);
      }
    }
  }, [objectStructure, objectType, setActiveObjectElements, activeObjectData, isReduxStateSynced]);

  // Update Redux tab state when tab changes (needed for filters)
  useEffect(() => {
    const currentTabConfig = OBJECT_TABS[activeTab];
    if (currentTabConfig) {
      const docTabType = {
        [TAB_IDS.LAYOUTS]: DocumentationTabTypes.LAYOUTS,
        [TAB_IDS.SALESFORCE_AUTOMATIONS]: DocumentationTabTypes.SF_AUTOMATIONS,
        [TAB_IDS.APEX]: DocumentationTabTypes.APEX,
        [TAB_IDS.VALIDATION_RULES]: DocumentationTabTypes.VALIDATION_RULES,
      }[currentTabConfig.id];

      if (docTabType) {
        dispatch(setTab({ tab: docTabType }));
      }
    }
  }, [activeTab, dispatch]);

  // Get data for current tab
  const currentTabConfig = OBJECT_TABS[activeTab];
  const currentTabData = useMemo(() => {
    if (!currentTabConfig) return [];

    switch (currentTabConfig.id) {
      case TAB_IDS.LAYOUTS:
        return getSafeActiveObjectData(DocumentationTabTypes.LAYOUTS);

      case TAB_IDS.SALESFORCE_AUTOMATIONS:
        const sfAutomations = getSafeActiveObjectData(DocumentationTabTypes.SF_AUTOMATIONS);
        if (sfAutomations.length > 0) {
          return filterItemsByTab(
            sfAutomations,
            currentTabConfig.types,
            TAB_IDS.SALESFORCE_AUTOMATIONS,
          );
        }
        // Fallback to parsed rules
        const automationRules = (parsedRules || []).filter(
          (rule: ParsedRule) =>
            rule.objectApiNames?.includes(objectType) &&
            [
              ParserBaseRuleType.flows,
              ParserBaseRuleType.approvalProcesses,
              ParserBaseRuleType.workflowRules,
              ParserBaseRuleType.processBuilderFlows,
            ].includes(rule.type),
        );
        return filterItemsByTab(
          automationRules,
          currentTabConfig.types,
          TAB_IDS.SALESFORCE_AUTOMATIONS,
        );

      case TAB_IDS.APEX:
        const apexData = getSafeActiveObjectData(DocumentationTabTypes.APEX);
        if (apexData.length > 0) {
          return filterItemsByTab(apexData, currentTabConfig.types, TAB_IDS.APEX);
        }
        const apexRules = (parsedRules || []).filter(
          (rule: ParsedRule) =>
            rule.objectApiNames?.includes(objectType) &&
            [ParserBaseRuleType.apexClasses, ParserBaseRuleType.apexTriggers].includes(rule.type),
        );
        return filterItemsByTab(apexRules, currentTabConfig.types, TAB_IDS.APEX);

      case TAB_IDS.VALIDATION_RULES:
        const validationData = getSafeActiveObjectData(DocumentationTabTypes.VALIDATION_RULES);
        if (validationData.length > 0) {
          return filterItemsByTab(validationData, currentTabConfig.types, TAB_IDS.VALIDATION_RULES);
        }
        const validationRules = (parsedRules || []).filter(
          (rule: ParsedRule) =>
            rule.objectApiNames?.includes(objectType) &&
            rule.type === ParserBaseRuleType.validationRules,
        );
        return filterItemsByTab(validationRules, currentTabConfig.types, TAB_IDS.VALIDATION_RULES);

      default:
        return [];
    }
  }, [currentTabConfig, getSafeActiveObjectData, parsedRules, objectType]);

  const searchFilteredTabData = useMemo(() => {
    const searchString = typeof searchText === 'string' ? searchText : '';
    if (!searchString?.trim()) return currentTabData;

    // Apply search filtering using the same logic as documentation
    return (
      filterItemsByQuerySearch({
        itemProps: ['name', 'label'],
        searchTxt: searchString,
        items: currentTabData,
      }) || []
    );
  }, [currentTabData, searchText]);

  const columns = useMemo(() => {
    if (!currentTabConfig) return [];

    const docTabType = {
      layouts: DocumentationTabTypes.LAYOUTS,
      salesforceAutomations: DocumentationTabTypes.SF_AUTOMATIONS,
      apex: DocumentationTabTypes.APEX,
      validationRules: DocumentationTabTypes.VALIDATION_RULES,
    }[currentTabConfig.id];

    if (!docTabType) return [];
    return getColumns(docTabType);
  }, [currentTabConfig]);

  const handleTabSelectionChange = (selectedIds: string[]) => {
    const tabId = currentTabConfig.id;

    // Convert row IDs back to names for storage
    const convertedToNames: string[] = [];
    selectedIds.forEach((selectedId) => {
      const matchingRow = searchFilteredTabData?.find((row: any) => {
        const rowId = row.id || row.metadataId || row.name;
        return rowId === selectedId;
      });
      if (matchingRow && matchingRow.name) {
        convertedToNames.push(matchingRow.name);
      }
    });

    const newTabSelections = {
      ...tabSelections,
      [tabId]: convertedToNames,
    };
    setTabSelections(newTabSelections);

    const allSelections: SelectionItem[] = [];

    OBJECT_TABS.forEach((tab) => {
      const tabSelectedIds = newTabSelections[tab.id] || [];
      const tabData = getTabData(tab.id);

      tabSelectedIds.forEach((selectedId) => {
        if (!selectedId) {
          return;
        }

        const item = tabData.find((item: ConfigurationItem) => {
          return item.name === selectedId; // Compare by name since we store names
        });

        if (item) {
          allSelections.push(createObjectSelectionItem(item, tab.id));
        }
      });
    });

    onSelectionChange(allSelections);
  };

  const getTabData = (tabId: string) => {
    const tab = OBJECT_TABS.find((t) => t.id === tabId);
    if (!tab) return [];

    switch (tabId) {
      case TAB_IDS.LAYOUTS:
        return getSafeActiveObjectData(DocumentationTabTypes.LAYOUTS);
      case TAB_IDS.SALESFORCE_AUTOMATIONS:
        const sfAutomations = getSafeActiveObjectData(DocumentationTabTypes.SF_AUTOMATIONS);
        return filterItemsByTab(sfAutomations, tab.types);
      case TAB_IDS.APEX:
        const apexData = getSafeActiveObjectData(DocumentationTabTypes.APEX);
        return filterItemsByTab(apexData, tab.types);
      case TAB_IDS.VALIDATION_RULES:
        const validationData = getSafeActiveObjectData(DocumentationTabTypes.VALIDATION_RULES);
        return filterItemsByTab(validationData, tab.types);
      default:
        return [];
    }
  };

  // Convert name-based selections to actual row IDs for the table
  const currentSelectedIds = useMemo(() => {
    const nameBasedSelections = tabSelections[currentTabConfig?.id] || [];

    if (nameBasedSelections.length === 0 || !searchFilteredTabData) {
      return nameBasedSelections;
    }

    // Convert names to actual row IDs
    const convertedIds: string[] = [];
    nameBasedSelections.forEach((selectionName) => {
      const matchingRow = searchFilteredTabData.find((row: any) => row.name === selectionName);
      if (matchingRow) {
        const rowId = matchingRow.id || matchingRow.metadataId || matchingRow.name;
        convertedIds.push(rowId);
      }
    });
    return convertedIds;
  }, [tabSelections, currentTabConfig?.id, searchFilteredTabData]);

  const enhancedColumns = useSelectableColumns(columns, searchFilteredTabData, {
    selectedRowIds: currentSelectedIds,
    onSelectionChange: handleTabSelectionChange,
  });

  const handleRowClick = (
    event: React.MouseEvent,
    row: DataTableRow,
    column: DataTableBaseColumn,
  ) => {
    if (isSubmitting) return;

    if (column.field === '__selection') {
      return;
    }
    const rowId = row.id || row.metadataId || row.name;
    if (!rowId) {
      return;
    }

    const isSelected = currentSelectedIds.includes(rowId);

    if (isSelected) {
      const newSelection = currentSelectedIds.filter((id) => id !== rowId);
      handleTabSelectionChange(newSelection);
    } else {
      const newSelection = [...currentSelectedIds, rowId];
      handleTabSelectionChange(newSelection);
    }
  };

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    OBJECT_TABS.forEach((tab) => {
      let tabData = [];

      switch (tab.id) {
        case TAB_IDS.LAYOUTS:
          tabData = getSafeActiveObjectData(DocumentationTabTypes.LAYOUTS);
          break;
        case TAB_IDS.SALESFORCE_AUTOMATIONS:
          const sfAutomations = getSafeActiveObjectData(DocumentationTabTypes.SF_AUTOMATIONS);
          tabData = filterItemsByTab(sfAutomations, tab.types, TAB_IDS.SALESFORCE_AUTOMATIONS);
          break;
        case TAB_IDS.APEX:
          const apexData = getSafeActiveObjectData(DocumentationTabTypes.APEX);
          if (apexData.length > 0) {
            tabData = filterItemsByTab(apexData, tab.types, TAB_IDS.APEX);
          } else {
            const apexRules = (parsedRules || []).filter(
              (rule: ParsedRule) =>
                rule.objectApiNames?.includes(objectType) &&
                [ParserBaseRuleType.apexClasses, ParserBaseRuleType.apexTriggers].includes(
                  rule.type,
                ),
            );
            tabData = filterItemsByTab(apexRules, tab.types, TAB_IDS.APEX);
          }
          break;
        case TAB_IDS.VALIDATION_RULES:
          const validationData = getSafeActiveObjectData(DocumentationTabTypes.VALIDATION_RULES);
          if (validationData.length > 0) {
            tabData = filterItemsByTab(validationData, tab.types, TAB_IDS.VALIDATION_RULES);
          } else {
            const validationRules = (parsedRules || []).filter(
              (rule: ParsedRule) =>
                rule.objectApiNames?.includes(objectType) &&
                rule.type === ParserBaseRuleType.validationRules,
            );
            tabData = filterItemsByTab(validationRules, tab.types, TAB_IDS.VALIDATION_RULES);
          }
          break;
      }

      counts[tab.id] = tabData?.length || 0;
    });
    return counts;
  }, [getSafeActiveObjectData, parsedRules, objectType]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (isSubmitting) return;
    setManualActiveTab(newValue);
  };

  const topBarProps = useMemo(
    () => ({
      label: '',
      filtersJsx: (
        <DocumentationFilters
          key={`agent-selection-${objectType}-${activeTab}`}
          crmOrgId={crmOrgId}
        />
      ),
      searchPlaceholder: 'Search',
      itemsCount: currentTabData?.length || 0,
      showResultsCount: true,
      allowColumnsManipulation: false,
      isExpandedMode: true,
    }),
    [crmOrgId, objectType, activeTab, currentTabData?.length],
  );

  if (!currentTabConfig) return null;

  return (
    <Stack spacing={3} sx={{ width: '100%', height: '100%', flex: 1 }}>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'grey.120',
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange}>
          {OBJECT_TABS.map((tab) => (
            <Tab
              key={tab.id}
              label={tabCounts[tab.id] > 0 ? `${tab.label} (${tabCounts[tab.id]})` : tab.label}
            />
          ))}
        </Tabs>
      </Box>

      <TopBarRenderer {...topBarProps} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <CustomColumnsTableContextProvider
          value={{
            tableId: `agent-selection-${currentTabConfig.id}` as DataTableId,
            columns: enhancedColumns,
            rows: currentTabData,
          }}
        >
          <Box sx={{ width: '100%', height: '100%', overflowX: 'auto' }}>
            <VirtualScrollCustomColumnsTable
              tableId={`agent-selection-${currentTabConfig.id}` as DataTableId}
              rows={searchFilteredTabData}
              onRowClick={isSubmitting ? undefined : handleRowClick}
              hideChevronButton={true}
              tableEmptyStateJsx={<TableEmptyState />}
              isLoading={isParsingObject}
            />
          </Box>
        </CustomColumnsTableContextProvider>
      </Box>
    </Stack>
  );
};
